(ns nosco.richtext.urls
  "Deal with urls within a PM document.

  On paste, run the text through a bunch of regexes,
  and if urls are found, create <a> tags with them.

  With liberal copying from:
  https://github.com/ueberdosis/tiptap/blob/main/packages/extension-link/src/link.ts
  "
  (:require
   ["nosco-prosemirror/markPasteRule" :default markPasteRule]
   ["nosco-prosemirror/url_regexes" :as url-regexes]
   ["nosco-prosemirror/utils" :refer [locateMark]]
   ["prosemirror-model" :refer [Fragment Slice]]
   ["react" :as React]
   [clojure.string :as string]
   [goog.object :as gobj]
   [hx.hooks :refer [useLayoutEffect useState]]
   [hx.react :as hx :refer [defnc]]
   [nosco.i18n.translate :as t]
   [nosco.ui.banners :as banners]
   [nosco.ui.checkbox :as checkbox]
   [nosco.ui.forms :as forms]
   [nosco.ui.inputs :as inputs]
   [nosco.ui.modals3 :as modals3]
   [nosco.util :as util]
   [prosemirror-state :as prosemirror-state]))

;; TODO some interesting decisions on behaviour here.
;; For example, if you select something which already has a link,
;; is it an edit or an add?
;; Also, how do you delete a link? No easy way other than deleting
;; and starting over. For discussion...
(defnc LinkEditorModal [{:keys [^js view showing on-close link-mark-type]}]
  ;; A few different behaviours here:
  ;; 1. No selection, no active link: Insert the new link with new text at cursor
  ;; 2. Some selection, no active link: Same as (1) but pre-populate with selected text
  ;; 3. No selection, active link: replace the entire link
  ;; 4. Some selection, active link: same as 3
  (let [[default-text set-default-text] (useState "")
        [curr-href set-curr-href] (useState "")
        [default-target set-default-target] (useState nil)
        [mode set-mode] (useState :add)
        [error set-error] (useState nil)
        form-ref (React/useRef)
        link-at-cursor
        (fn ^{:doc
              "Return information about the link at the cursor, if any.
              Note that this returns nil if there is an actual selection."}
          link-at-cursor
          []
          (let [lac (locateMark (.. view -state -selection)
                                link-mark-type)]
            (when lac
              {:link (.-mark lac)
               :text (.-text lac)
               :target (.-mark.attrs.target lac)
               :range {:from  (.-from (.-range lac))
                       :to (.-to (.-range lac))}})))

        create-link-node
        (fn
          ^{:doc
            "Create a link node with the href and title of the form.
            If any are blank, return nil"}
          create-link-node
          []
          (let [fields (forms/form-data (.-current form-ref))
                href (get fields "href")
                href (util/email->mailto href)
                title (get fields "title")
                target (get fields "target")
                link (.create link-mark-type #js {:href href
                                                  :title title
                                                  :target (when target "_blank")})
                schema (.. view -state -schema)]
            (when-not (or (string/blank? href)
                          (string/blank? title))
              (.text schema title #js [link]))))
        edit-link (fn ^{:doc
                        "Edit the current link at the cursor, with the
                        values of the modal. Returns truthy if successful, falsy otherwise."}
                    []
                    (let [{:keys [range]} (link-at-cursor)
                          node (create-link-node)]
                      (when (and node range)
                        (let
                          [tr (.. view -state -tr)
                           fragment (.from Fragment node)
                           slice (.maxOpen Slice fragment)
                           tr (.replaceRange tr
                                             (:from range)
                                             (:to range) slice)]
                          ((.-dispatch view) tr)
                          true))))
        add-link (fn ^{:doc
                   "Add a new link replacing the selection, with the
                   values of the modal. Return truthy if successful, falsy otherwise"}
                   []
                   (when-let [node (create-link-node)]
                     (let [tr (.. view -state -tr)
                           tr (.replaceSelectionWith tr node false)]
                       ((.-dispatch view) tr)
                       true)))
        confirm (fn []
                  (if (= mode :edit)
                    (edit-link)
                    (add-link)))
        submit (fn []
                 (if (confirm)
                   (on-close)
                   (set-error (t/t "Please fill in the form"))))]
    (useLayoutEffect
      (fn []
        ;; whenever the modal shows, populate the
        ;; Text to Display field with the either
        ;; a. the current selected text, if no link is selected
        ;; b. the current link text, if cursor is on a link
        ;; c. the current href, if cursor is on a link
        (when showing
          (set-error nil))
        (when view
          (let [closest-link (link-at-cursor)]
            (if closest-link
              (let [{:keys [link text]} closest-link
                    attrs (.-attrs link)
                    href (.-href attrs)
                    target (.-target attrs)]
                (set-mode :edit)
                (set-default-text text)
                (set-curr-href href)
                (set-default-target (boolean target)))
              (let [selection (.. view -state -selection)
                    fragment (.. selection content -content)
                    size (.-size fragment)
                    text (.textBetween fragment 0 size)]
                (set-mode :add)
                (set-default-text text)
                (set-curr-href "")
                (set-default-target true)))))
        js/undefined )
      [showing view])
    [modals3/ModalDialog {:showing showing
                          :title  (case mode
                                    :add (t/t "Add link")
                                    :edit (t/t "Update link"))
                          :on-close on-close
                          :cancel-label (t/t "Cancel")
                          :confirm-label (case mode
                                           :add (t/t "Add link")
                                           :edit (t/t "Update link"))
                          :on-confirm #(submit)
                          :size :narrow}
     [:form {:ref form-ref :on-submit (fn [event] (.preventDefault event) (submit))}
      [:input {:type "submit" :style {:display "none"}}]
      (when error
        [banners/CriticalBanner {:class "mb-spacing-16"}
         [banners/BannerTitle error]])
      [inputs/LabelledTextInput {:label (t/t "URL")
                                 :default-value curr-href
                                 :key (str "href:" curr-href)
                                 :name "href"
                                 :placeholder (t/t "Paste or type URL")
                                 :required true
                                 :auto-focus true}]
      [:div {:class "mt-spacing-24"}]
      [inputs/LabelledTextInput {:label (t/t "Text to display")
                                 :name "title"
                                 :key (str "title:" default-text)
                                 :required true
                                 :default-value default-text}]
      [:div {:class "mt-spacing-24"}]
      (let [[checked ->checked] (useState default-target)]
        ;; default-target is set multiple times so we have to update check not
        ;; only with the initial value
        (hx.hooks/useEffect (fn []
                              (->checked default-target))
                            [default-target])
        [checkbox/CheckboxWithLabel {:label (t/t "Open link in new tab")
                                     :name "target"
                                     :container-props {:style {:padding-left 0}}
                                     :on-change #(->checked (not checked))
                                     :checked checked}])]]))


(defn make-pasted-urls-links-plugin
  "This plugin detects URLs in pasted text, and converts the URLs into links.
   It doesn't change the behaviour of the paste, just transforms the pasted content
   slightly."
  [link-mark]
  (markPasteRule
   "auto-link-plugin-key"
   url-regexes/pasteRegex
   link-mark
   (fn [match]
     #js {:href (util/email->mailto (first match))
          :target "_blank"})
   (fn [fragment]
     fragment)))



(defonce paste-links-plugin-key (prosemirror-state/PluginKey. "nosco-paste-links"))


(defn can-convert-to-link? [^js selection]
  (let [fragment (.-content (.content selection))
        child-count (.-childCount fragment)
        text (.textBetween fragment 0 (.-size fragment))]
    (cond
      (.-empty selection) false
      (not= 1 child-count) false
      (string/blank? text) false
      :else true)))

(defn handle-pasted-link [^js view event slice]
  (let [^js fragment (.-content slice)
        child-count (.-childCount fragment)
        schema (.. view -state -schema)
        link-mark-type (gobj/get (.-marks schema) "link")
        tr  (.-tr  (.-state view))
        selection  (.-selection tr)]
    (if (or (not (can-convert-to-link? selection))
            (not= 1 child-count))
      false
      (let [text (.textBetween fragment 0 (.-size fragment))
            href (re-find url-regexes/pasteRegexExact text)]
        (if-not href
          false
          (let [link (.create link-mark-type #js {:href (util/email->mailto href)
                                                  :target "_blank"})
                selection-text (.textBetween (.-content (.content selection))
                                             0 (.-size (.-content (.content selection))))
                node (.text schema selection-text #js [link])
                tr (.replaceSelectionWith tr node false)]
            ((.-dispatch view) tr)
            true))))))

(defn handle-pasted-urls-selection-plugin 
  "This plugin adds a behaviour where if some text is selected, and a single
   url is pasted, then, instead of the default behaviour of replacing the text
  with the URL, it will actually convert the text into a link, keeping the text
  the same and adding the URL as the href."
  []
  (let [spec #js {:props #js {:handlePaste handle-pasted-link}
                  :key paste-links-plugin-key}]
    (prosemirror-state/Plugin. spec)))
